import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ENTITIES } from '../data';

type NodeSize = 'md' | 'lg' | 'xl';

interface NodeProps {
  id: string;
  style: CSSProperties;
  size?: NodeSize;
}

function Node({ id, style, size = 'md' }: NodeProps) {
  const e = ENTITIES[id];
  return (
    <Link className={`flow-node flow-node-${size}`} style={style} to={`/docs/concepts/${id}`}>
      <div className="flow-node-name">{e.name}</div>
      <div className="flow-node-line">{e.oneLiner}</div>
    </Link>
  );
}

export function FlowSchema() {
  return (
    <div className="flow-schema">
      <div className="flow-bg-glow" />

      <div className="flow-col-headers">
        <div>
          <span className="rv-eyebrow-mini">Catalog &amp; pricing</span>
          <div className="flow-col-sub">Built and priced once, up front</div>
        </div>
        <div>
          <span className="rv-eyebrow-mini">The subscription</span>
          <div className="flow-col-sub">Pins versions, resolves the price</div>
        </div>
        <div>
          <span className="rv-eyebrow-mini">Billing &amp; analytics</span>
          <div className="flow-col-sub">What happens next</div>
        </div>
      </div>

      <svg className="flow-connectors" viewBox="0 0 1600 1040" preserveAspectRatio="none">
        <defs>
          <linearGradient id="flowLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(0,217,166,0.08)" />
            <stop offset="50%" stopColor="rgba(0,217,166,0.5)" />
            <stop offset="100%" stopColor="rgba(0,217,166,0.08)" />
          </linearGradient>
          <marker
            id="flowPinArrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto"
          >
            <path d="M0 0 L10 5 L0 10 z" fill="rgba(0,217,166,0.75)" />
          </marker>
        </defs>
        {/* Catalog & pricing converge into the Subscription hero — the
            Subscription pins specific catalog versions and resolves their
            price; the catalog is configured up front, not downstream of it. */}
        <path d="M300 165 C 500 165 560 500 740 500" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M300 260 C 500 260 560 500 740 500" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M300 355 C 500 355 560 500 740 500" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M300 565 C 500 565 560 500 740 500" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M300 660 C 500 660 560 500 740 500" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M300 755 C 500 755 560 500 740 500" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M300 850 C 500 850 560 500 740 500" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        {/* Emphasised pin edge: the Subscription pins the Variable Component
            version it bills against. Still left-to-right (into the hero), so
            the component reads as part of what the subscription references. */}
        <path
          d="M300 450 C 500 450 560 500 720 500"
          stroke="rgba(0,217,166,0.6)"
          strokeWidth="2"
          strokeDasharray="6 6"
          fill="none"
          markerEnd="url(#flowPinArrow)"
        />
        {/* Within the subscription zone: a Client owns the Subscription, which
            accrues Expansions over its life. */}
        <path d="M890 322 C 893 390 897 410 900 448" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M900 552 C 898 612 892 640 890 678" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        {/* Subscription emits invoices. Discounts are applied as invoices resolve. */}
        <path d="M1040 500 C 1120 500 1140 315 1200 315" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M1040 480 C 1100 480 1140 450 1200 450" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        {/* Discounts reduce the invoice amount before it's emitted. */}
        <path d="M1250 480 C 1270 550 1260 620 1240 680" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
        <path d="M1040 500 C 1120 500 1140 715 1200 715" stroke="url(#flowLine)" strokeWidth="1.5" fill="none" />
      </svg>

      <Node id="region" style={{ left: 40, top: 130 }} />
      <Node id="featureFlag" style={{ left: 40, top: 225 }} />
      <Node id="meter" style={{ left: 40, top: 320 }} />
      <Node id="variableComponent" style={{ left: 40, top: 415 }} />
      <Node id="module" style={{ left: 40, top: 530 }} />
      <Node id="bundle" style={{ left: 40, top: 625 }} />
      <Node id="variableComponentPrice" style={{ left: 40, top: 720 }} />
      <Node id="customPricingAgreement" style={{ left: 40, top: 815 }} />

      <Node id="client" style={{ left: 760, top: 250 }} />
      <Node id="subscription" style={{ left: 760, top: 450 }} size="xl" />
      <Node id="expansion" style={{ left: 760, top: 680 }} />

      <Node id="invoice" style={{ left: 1200, top: 280 }} />
      <Node id="discount" style={{ left: 1200, top: 450 }} />
      <Node id="lifecycleEvent" style={{ left: 1200, top: 680 }} />

      <div className="flow-tenant">
        <span className="rv-eyebrow-mini" style={{ color: 'var(--rv-accent)' }}>
          Everything below lives inside one
        </span>
        <Link className="flow-tenant-btn" to="/docs/concepts/tenant">
          Tenant ›
        </Link>
      </div>

      <div className="flow-pin-label">
        <span className="flow-pin-eyebrow">Pin</span>
        <strong>The subscription pins the catalog</strong>
        <span>
          A Subscription pins specific <code>(componentId, componentVersion)</code> tuples and
          resolves their scoped price — the catalog and pricing are built once, then referenced.
        </span>
      </div>

      <Link className="flow-annotation" to="/docs/concepts/exchangeRate">
        <span>⇄</span>
        <div>
          <strong>Exchange rates</strong>
          <span>Pulled daily. Subscriptions store the rate they used.</span>
        </div>
      </Link>
    </div>
  );
}
